import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import WysiwygEditor from '../formComponents/WysiwygEditor';
import TextField from '../formComponents/TextField';
import PMButton from "../../common/Button"

import { Box, Paper, Typography } from "@material-ui/core"
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import { updateFormField } from '../../../actions/forms';

import _ from "lodash"

const useStyles = makeStyles({
    paper: {
        padding: "2.5rem",
        margin: "1.5rem 0rem",
        borderRadius: "16px"
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem'
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1a1a1a',
        marginBottom: '0.25rem'
    },
    subtitle: {
        fontSize: '0.875rem',
        color: '#717171',
        maxWidth: '600px',
        lineHeight: 1.5
    },
    emptyState: {
        padding: '3rem 2rem',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        border: '1px dashed #e0e0e0'
    },
    emptyTitle: {
        fontSize: '1rem',
        fontWeight: 500,
        color: '#484848',
        marginBottom: '0.5rem'
    },
    emptySubtitle: {
        fontSize: '0.875rem',
        color: '#717171',
        marginBottom: '1.5rem'
    },
    sectionCard: {
        padding: '1.5rem',
        marginBottom: '1rem',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        border: '1px solid #ebebeb',
        '&:last-child': {
            marginBottom: 0
        }
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
    },
    sectionNumber: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#717171',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    deleteButton: {
        color: '#b0b0b0',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#ff5a5f',
            backgroundColor: '#fff5f5'
        }
    },
    fieldLabel: {
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: '#484848',
        marginBottom: '0.5rem'
    },
    contentSection: {
        marginTop: '1.25rem'
    },
    addButton: {
        minWidth: 'auto',
        whiteSpace: 'nowrap'
    }
});

const labels = {
    english: {
        title: 'Unassigned Sections',
        description: 'Custom sections that appear after Patient Medication Information.',
        sectionTitle: 'Section Title',
        sectionContent: 'Content',
        addSection: 'Add Section',
        section: 'Section',
        emptyTitle: 'No sections added yet',
        emptySubtitle: 'Add custom sections for content not covered by the standard template.',
        titlePlaceholder: 'e.g., Instructions for Use'
    },
    french: {
        title: 'Sections non attribuées',
        description: 'Sections personnalisées après les renseignements sur le médicament.',
        sectionTitle: 'Titre de la section',
        sectionContent: 'Contenu',
        addSection: 'Ajouter',
        section: 'Section',
        emptyTitle: 'Aucune section ajoutée',
        emptySubtitle: 'Ajoutez des sections pour le contenu non couvert par le modèle standard.',
        titlePlaceholder: 'p. ex., Mode d\'emploi'
    }
}

export default function UnassignedSections({ context }) {
    const dispatch = useDispatch()
    const form = useSelector((state) => state.forms.form)
    const updating = useSelector((state) => state.forms.updating)
    const classes = useStyles();

    const currentLabels = context ? labels.french : labels.english
    let formInput = context ? form && form.formDataFrench : form && form.formDataEnglish

    const unassignedSections = formInput?.unassignedSections || []

    const handleUpdateField = (name, content) => {
        _.set(formInput, name, content)
        dispatch(updateFormField(name, content, form._id, context))
    }

    const handleAddSection = () => {
        const newSections = [...unassignedSections, { title: '', content: '' }]
        dispatch(updateFormField("unassignedSections", newSections, form._id, context))
    }

    const handleDeleteSection = (indexToDelete) => {
        const newSections = unassignedSections.filter((_, index) => index !== indexToDelete)
        dispatch(updateFormField("unassignedSections", newSections, form._id, context))
    }

    return (
        <Box mt={4}>
            <Paper className={classes.paper} elevation={4}>
                <Box className={classes.header}>
                    <Box>
                        <Typography className={classes.title}>
                            {currentLabels.title}
                        </Typography>
                        <Typography className={classes.subtitle}>
                            {currentLabels.description}
                        </Typography>
                    </Box>
                    <Box className={classes.addButton}>
                        <PMButton
                            isFormButton={true}
                            disabled={updating}
                            onClick={handleAddSection}
                        >
                            <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
                            {currentLabels.addSection}
                        </PMButton>
                    </Box>
                </Box>

                {unassignedSections.length > 0 ? (
                    <Box>
                        {unassignedSections.map((section, index) => (
                            <Box key={`unassigned-section-${index}-${context}`} className={classes.sectionCard}>
                                <Box className={classes.sectionHeader}>
                                    <Typography className={classes.sectionNumber}>
                                        {currentLabels.section} {index + 1}
                                    </Typography>
                                    <DeleteOutlineIcon
                                        className={classes.deleteButton}
                                        onClick={() => !updating && handleDeleteSection(index)}
                                    />
                                </Box>

                                <Box>
                                    <Typography className={classes.fieldLabel}>
                                        {currentLabels.sectionTitle}
                                    </Typography>
                                    <TextField
                                        updateFunction={handleUpdateField}
                                        placeholder={currentLabels.titlePlaceholder}
                                        name={`unassignedSections[${index}].title`}
                                        value={section.title || ''}
                                    />
                                </Box>

                                <Box className={classes.contentSection}>
                                    <Typography className={classes.fieldLabel}>
                                        {currentLabels.sectionContent}
                                    </Typography>
                                    <WysiwygEditor
                                        language={context}
                                        initialValue={section.content || ''}
                                        name={`unassignedSections[${index}].content`}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box className={classes.emptyState}>
                        <Typography className={classes.emptyTitle}>
                            {currentLabels.emptyTitle}
                        </Typography>
                        <Typography className={classes.emptySubtitle}>
                            {currentLabels.emptySubtitle}
                        </Typography>
                        <Box display="inline-block">
                            <PMButton
                                isFormButton={true}
                                disabled={updating}
                                onClick={handleAddSection}
                            >
                                <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
                                {currentLabels.addSection}
                            </PMButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    )
}
